import { useState } from 'react';
import { useStepper } from "@/components/StepperProvider";
import { StepperNavigation } from "@/components/StepperNavigation";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { ChevronDown } from 'lucide-react';

const WorldBuildingStep = () => {
    const { formData, updateFormData } = useStepper();
    const [worldData, setWorldData] = useState({
        setting: formData?.setting || '',
        timeOfDay: formData?.timeOfDay || '',
        weather: formData?.weather || '',
        location: formData?.location || '',
        customElements: formData?.customElements || '',
        atmosphere: formData?.atmosphere || ''
    });

    const handleInputChange = (field: string, value: string) => {
        setWorldData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNext = () => {
        updateFormData({ ...formData, ...worldData });
    };

    const isValid = (worldData.setting && worldData.setting !== "custom") || worldData.location;

    return (
        <div className="space-y-6">
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="setting">Where does your story take place?</Label>
                        <Select
                            value={worldData.setting}
                            onValueChange={(value) => handleInputChange('setting', value)}
                        >
                            <SelectTrigger id="setting">
                                <SelectValue placeholder="Choose a setting" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="custom">Custom Location</SelectItem>
                                <SelectItem value="forest">Enchanted Forest</SelectItem>
                                <SelectItem value="castle">Magical Castle</SelectItem>
                                <SelectItem value="ocean">Deep Ocean</SelectItem>
                                <SelectItem value="space">Outer Space</SelectItem>
                                <SelectItem value="city">Bustling City</SelectItem>
                                <SelectItem value="farm">Cozy Farm</SelectItem>
                                <SelectItem value="mountains">Misty Mountains</SelectItem>
                                <SelectItem value="jungle">Wild Jungle</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {worldData.setting === 'custom' && (
                        <div>
                            <Label htmlFor="customSetting">Describe your setting</Label>
                            <Input
                                id="customSetting"
                                value={worldData.location}
                                onChange={(e) => handleInputChange('location', e.target.value)}
                                placeholder="Example: A floating candy island, Central Park"
                            />
                        </div>
                    )}

                    <Accordion type="single" collapsible>
                        <AccordionItem value="details">
                            <AccordionTrigger className="hover:no-underline [&[data-state=open]>div>div>.expand-arrow]:rotate-180">
                                <div className="flex items-center w-full">
                                    <div className="flex items-center underline">
                                        Add more details (optional)
                                        <ChevronDown className="w-4 h-4 ml-2 transition-transform duration-200 expand-arrow" />
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="space-y-4">
                                <div>
                                    <Label htmlFor="timeOfDay">Time of Day</Label>
                                    <Select
                                        value={worldData.timeOfDay}
                                        onValueChange={(value) => handleInputChange('timeOfDay', value)}
                                    >
                                        <SelectTrigger id="timeOfDay">
                                            <SelectValue placeholder="When does the story happen?" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="morning">Early Morning</SelectItem>
                                            <SelectItem value="day">Bright Day</SelectItem>
                                            <SelectItem value="sunset">Sunset</SelectItem>
                                            <SelectItem value="night">Starry Night</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label htmlFor="weather">Weather</Label>
                                    <Select
                                        value={worldData.weather}
                                        onValueChange={(value) => handleInputChange('weather', value)}
                                    >
                                        <SelectTrigger id="weather">
                                            <SelectValue placeholder="How's the weather?" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="sunny">Sunny</SelectItem>
                                            <SelectItem value="rainy">Rainy</SelectItem>
                                            <SelectItem value="snowy">Snowy</SelectItem>
                                            <SelectItem value="cloudy">Cloudy</SelectItem>
                                            <SelectItem value="stormy">Stormy</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label htmlFor="customElements">Custom Elements</Label>
                                    <Textarea
                                        id="customElements"
                                        value={worldData.customElements}
                                        onChange={(e) => handleInputChange('customElements', e.target.value)}
                                        placeholder="Any special or magical elements in your world? (e.g., No Gravity, Talking Trees)"
                                        className="h-20"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="atmosphere">Atmosphere</Label>
                                    <Textarea
                                        id="atmosphere"
                                        value={worldData.atmosphere}
                                        onChange={(e) => handleInputChange('atmosphere', e.target.value)}
                                        placeholder="How does your world feel? (e.g., cozy, mysterious, exciting)"
                                        className="h-20"
                                    />
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>

            <StepperNavigation
                onNext={handleNext}
                nextDisabled={!isValid}
            />
        </div>
    );
};

export default WorldBuildingStep;
